import { FC } from "react";
import { motion } from "framer-motion";
import {
  PlusCircle,
  Filter,
  CheckCircle,
  Clock,
  FileText,
  XCircle,
  Loader,
  Building2,
  TrendingUp,
  Eye,
} from "lucide-react";

export interface ModernApprovedSpacesHeaderProps {
  totalCount: number;
  approvedCount: number;
  pendingCount: number;
  draftCount: number;
  blockedCount: number;
  processingCount: number;
  onAddSpace: () => void;
  onFilter: (status?: string) => void;
}

const ModernApprovedSpacesHeader: FC<ModernApprovedSpacesHeaderProps> = ({
  totalCount,
  approvedCount,
  pendingCount,
  draftCount,
  blockedCount,
  processingCount,
  onAddSpace,
  onFilter,
}) => {
  const stats = [
    {
      label: "Publiés",
      count: approvedCount,
      icon: <CheckCircle className="w-5 h-5" />,
      color: "from-green-500 to-emerald-600",
      bgColor: "bg-green-100 dark:bg-green-900/20",
      textColor: "text-green-700 dark:text-green-300",
    },
    {
      label: "En attente",
      count: pendingCount,
      icon: <Clock className="w-5 h-5" />,
      color: "from-orange-500 to-amber-600",
      bgColor: "bg-orange-100 dark:bg-orange-900/20",
      textColor: "text-orange-700 dark:text-orange-300",
    },
    {
      label: "Brouillons",
      count: draftCount,
      icon: <FileText className="w-5 h-5" />,
      color: "from-gray-500 to-slate-600",
      bgColor: "bg-gray-100 dark:bg-gray-900/20",
      textColor: "text-gray-700 dark:text-gray-300",
    },
    {
      label: "En cours",
      count: processingCount,
      icon: <Loader className="w-5 h-5" />,
      color: "from-blue-500 to-indigo-600",
      bgColor: "bg-blue-100 dark:bg-blue-900/20",
      textColor: "text-blue-700 dark:text-blue-300",
    },
    {
      label: "Bloqués",
      count: blockedCount,
      icon: <XCircle className="w-5 h-5" />,
      color: "from-red-500 to-rose-600",
      bgColor: "bg-red-100 dark:bg-red-900/20",
      textColor: "text-red-700 dark:text-red-300",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="mb-8"
    >
      {/* Main Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
        <div className="mb-6 lg:mb-0">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center space-x-3 mb-3"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent dark:from-white dark:to-gray-300">
                Mes Espaces
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Gérez vos {totalCount} espace{totalCount !== 1 ? "s" : ""} événementiel
                {totalCount !== 1 ? "s" : ""}
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-3"
        >
          <button
            onClick={() => onFilter()}
            className="flex items-center space-x-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <span className="text-gray-700 dark:text-gray-300 font-medium">Filtres</span>
          </button>

          <button
            onClick={onAddSpace}
            className="flex items-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <PlusCircle className="w-4 h-4" />
            <span className="font-semibold">Ajouter un espace</span>
          </button>
        </motion.div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index }}
            whileHover={{ scale: 1.02 }}
            className={`${stat.bgColor} rounded-xl p-4 cursor-pointer transition-all duration-200 hover:shadow-lg border border-gray-100 dark:border-gray-800`}
            onClick={() => onFilter(stat.label.toLowerCase())}
          >
            <div className="flex items-center justify-between mb-2">
              <div className={`p-2 rounded-lg ${stat.textColor} ${stat.bgColor}`}>{stat.icon}</div>
              <div className="text-right">
                <div className={`text-2xl font-bold ${stat.textColor}`}>{stat.count}</div>
              </div>
            </div>
            <div className={`text-sm font-medium ${stat.textColor}`}>{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex flex-wrap gap-3"
      >
        <button
          onClick={() => onFilter("approuvé")}
          className="flex items-center space-x-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors text-sm font-medium"
        >
          <Eye className="w-4 h-4" />
          <span>Voir les publiés</span>
        </button>

        <button
          onClick={() => onFilter("brouillon")}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900/30 transition-colors text-sm font-medium"
        >
          <FileText className="w-4 h-4" />
          <span>Reprendre les brouillons</span>
        </button>

        <button
          onClick={() => onFilter("en attente")}
          className="flex items-center space-x-2 px-4 py-2 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors text-sm font-medium"
        >
          <TrendingUp className="w-4 h-4" />
          <span>Suivre les demandes</span>
        </button>
      </motion.div>
    </motion.div>
  );
};

export default ModernApprovedSpacesHeader;
